import styled, { css } from 'styled-components';
import { CommonInteractionStyling } from '../common';
import { COLORS } from '../styles';

export const Dropdown = styled.div<{ isButton: boolean; locked?: boolean; disabled?: boolean }>`
  position: relative;
  display: inline-block;
  ${props => !props.isButton && 'width: 100%; min-width: 344px;'}
  ${props => (props.locked || props.disabled ? 'cursor: not-allowed;' : '')}

  .show {
    display: block;
  }

  svg {
    position: absolute;
    top: ${props => (props.isButton ? '8px' : '16px')};
    right: ${props => (props.isButton ? '8px' : '24px')};
    cursor: pointer;
  }

  input:hover + div {
    color: ${COLORS.primary_600};
  }

  input:active + div {
    color: ${COLORS.primary_800};
  }
`;

const lockedState = css`
  background-color: ${COLORS.neutral_100};
  box-shadow: inset 0px 0px 0px 1px ${COLORS.neutral_300};
  color: ${COLORS.neutral_600};
  pointer-events: none;
  cursor: not-allowed;
  input {
    color: ${COLORS.neutral_600} !important;
  }
`;

const disabledState = css`
  background-color: ${COLORS.white};
  box-shadow: inset 0px 0px 0px 1px ${COLORS.neutral_100};
  color: ${COLORS.neutral_300};
  pointer-events: none;
  cursor: not-allowed;
  svg {
    color: ${COLORS.neutral_300} !important;
  }
  input {
    color: ${COLORS.neutral_300} !important;
    ::placeholder {
      color: ${COLORS.neutral_300} !important;
    }
  }
`;

const isButtonStyledFieldState = css`
  height: 38px;
  width: initial;
  min-width: initial;
  padding: 8px 38px 0 12px;
  border: none !important;
  box-shadow: none !important;
  font-weight: bold;
  border-radius: 8px;
  color: ${COLORS.neutral_600};

  &.expanded {
    color: ${COLORS.neutral_800};
    background: ${COLORS.neutral_20};
  }
`;

const activeValidationMessage = css`
  -webkit-box-shadow: inset 0px 0px 0px 2px ${COLORS.warning_400};
  -moz-box-shadow: inset 0px 0px 0px 2px ${COLORS.warning_400};
  box-shadow: inset 0px 0px 0px 2px ${COLORS.warning_400};
`;

const placeholderStyling = css`
  font-family: 'Lato', sans-serif;
  font-style: italic;
  font-weight: normal;
  font-size: 18px;
  color: ${COLORS.neutral_600};
`;

export const StyledField = styled.div<{ disabled: boolean; locked: boolean; isButton: boolean; showValidationMessage?: boolean; isPlaceholder?: boolean }>`
  font-weight: normal;
  font-size: 18px;
  padding: 16px 20px 12px 16px;
  height: 56px;
  min-width: 344px;
  width: 100%;

  background: ${COLORS.white};
  box-shadow: inset 0px 0px 0px 1px ${COLORS.neutral_300};
  box-sizing: border-box;
  border-radius: 4px;
  cursor: pointer;

  &:focus {
    outline: 3px solid #ddd;
  }
  &:focus-within {
    -webkit-box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_800};
    -moz-box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_800};
    box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_800};
  }
  &:hover {
    box-shadow: inset 0px 0px 0px 1px ${COLORS.primary_200};
    input {
      ::placeholder {
        color: ${COLORS.primary_600};
      }
    }
    cursor: pointer;
  }
  &:active {
    box-shadow: inset 0px 0px 0px 1px ${COLORS.primary_300};
    input {
      ::placeholder {
        color: ${COLORS.primary_800};
      }
    }
  }

  ${props => (props.disabled ? disabledState : '')}
  ${props => (props.locked ? lockedState : '')}
  ${props => (props.isButton ? isButtonStyledFieldState : '')}
  ${props => (props.showValidationMessage ? activeValidationMessage : '')}
  ${props => (props.isPlaceholder ? placeholderStyling : '')}

  ${CommonInteractionStyling};
`;

const ButtonDropdownContentStyling = css`
  min-width: 344px;
  right: 0;
`;

export const DropdownContent = styled.div<{ isButton: boolean }>`
  display: none;
  position: absolute;
  background-color: ${COLORS.white};
  width: 100%;
  z-index: 1;

  &.show {
    display: block;
  }

  ${props => (props.isButton ? ButtonDropdownContentStyling : '')};

  border: 1px solid ${COLORS.neutral_100};
  box-sizing: border-box;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  padding: 4px 0;

  max-height: 240px;
  overflow-y: scroll;

  /* width */
  ::-webkit-scrollbar {
    width: 4px;
    cursor: pointer;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: ${COLORS.white};
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: ${COLORS.neutral_200};
    border-radius: 8px;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: ${COLORS.neutral_400};
  }
`;

export const DropdownButton = styled.button`
  color: ${COLORS.neutral_600};

  position: relative;
  text-decoration: none;
  display: block;
  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 120%;
  height: 48px;
  background-color: ${COLORS.white};
  border: none;
  cursor: pointer;
  background: transparent;
  width: 100%;
  text-align: left;

  span {
    margin: auto 0 auto 16px;
    line-height: 48px;
  }

  ${CommonInteractionStyling};

  &.active {
    background: ${COLORS.neutral_20};
    &::after {
      position: absolute;
      content: ' ';
      width: 4px;
      top: 0;
      bottom: 0;
      left: 2px;
      background-color: ${COLORS.primary_500};

      border-top-left-radius: 2px;
      border-top-right-radius: 2px;
      border-bottom-left-radius: 2px;
      border-bottom-right-radius: 2px;
    }
  }

  &.active:hover {
    background-color: ${COLORS.primary_20};
    svg {
      color: ${COLORS.primary_600};
    }
    &::after {
      background-color: ${COLORS.primary_500};
    }
  }
  &.active:active {
    background-color: ${COLORS.primary_100};
    svg {
      color: ${COLORS.primary_800};
    }

    &::after {
      background-color: ${COLORS.primary_500};
    }
  }
`;

export const Loading = styled.div`
  svg {
    margin-right: 32px;
  }
`;
